import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import {
  useAddNoteTopic,
  useCreateTopic,
  useDeleteEgo,
  useDeletePatternTopic,
  useGetPermissions,
  useGetTopicDetails,
  useUpdateNote,
  useUpdatePatternTopic,
  useUpdateVisibility,
  useUploadMedia,
} from '@/hooks/api/dashboard'
import { useLocalStorage } from '@/hooks/common'
import { Note, PatternDraft, PatternProps, PatternSelections, Topic } from '@/types'
import { Assets } from '@/utils/pattern-assets'
import { PatternNoteTypes, PrivacyType, ResourceType, TopicType } from '@/utils/constants'
import { SetTopicType, setApplyToAllActive } from '@/pages/pattern/renderer/main'

/**
 * Ported, gallery-only version of the load/save/rename/privacy/delete logic in
 * src/pages/pattern/index.tsx. Guest mode, text/Konva overlays, and the legacy
 * MUI chrome are intentionally dropped — GALLERY topics are always authenticated.
 */
export function useGalleryStudioData(galleryId: string) {
  const [ewUser] = useLocalStorage('ewUser', null)
  const navigate = useNavigate()

  const getTopicDetails = useGetTopicDetails()
  const createTopicNote = useAddNoteTopic()
  const updateTopicNote = useUpdateNote()
  const uploadPhoto = useUploadMedia()
  const deleteEgos = useDeleteEgo(galleryId)
  const deleteTopic = useDeletePatternTopic()
  const updateTopic = useUpdatePatternTopic()
  const updateVisibility = useUpdateVisibility()
  const getPermissions = useGetPermissions()
  const createTopic = useCreateTopic(ewUser?.userID)

  const [topic, setTopic] = useState<Topic | null>(null)
  const [isError, setIsError] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const [patternData, setPatternData] = useState<PatternDraft>({})
  const [draftData, setDraftData] = useState<PatternDraft | null>(null)
  const [changesCount, setChangesCount] = useState(0)
  const [isEditMode, setIsEditMode] = useState(true)
  const [currentTab, setCurrentTab] = useState<string>('template')
  const [selectedAssets, setSelectedAssets] = useState<PatternSelections>({
    matting: { width: 0 },
    clicked: false,
  })
  const [cameraHorizontalPosition, setCameraHorizontalPosition] = useState(0.5)
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null)
  const [screenshotModalOpen, setScreenshotModalOpen] = useState(false)

  const canvaRef = useRef<any>(null)
  const draftDataRef = useRef<PatternDraft | null>(null)
  const topicStateRef = useRef<Topic | null>(null)
  const templateChanged = useRef(false)
  const saveMode = useRef(0) // 1 screenshot only, 2 save, 3 back, 4 duplicate

  useEffect(() => {
    draftDataRef.current = draftData
  }, [draftData])

  useEffect(() => {
    topicStateRef.current = topic
  }, [topic])

  const load = useCallback(async () => {
    setLoading(true)
    setChangesCount(0)
    try {
      const topicData = await getTopicDetails.mutateAsync({ topicID: galleryId })

      if (topicData.topicType !== TopicType.GALLERY.toString()) {
        // Not a gallery — hand off to the legacy pattern editor instead
        navigate(`/pattern?topicID=${galleryId}`, { replace: true })
        return
      }

      setTopic(topicData)
      SetTopicType(topicData.topicType)
      setCurrentTab('template')

      const draft: PatternDraft = { templateID: 400 }
      const sortedNotes = [...topicData.notes].sort((a, b) => {
        const dateA = a.dateUpdated || a.dateCreated || ''
        const dateB = b.dateUpdated || b.dateCreated || ''
        return dateB.localeCompare(dateA)
      })
      const processedNames = new Set<string>()
      sortedNotes.forEach((note: Note) => {
        if (note.noteType === '8' || note.noteType === '9' || note.noteType === '1') return
        if (processedNames.has(note.noteName)) return
        processedNames.add(note.noteName)
        try {
          draft[note.noteName as keyof PatternDraft] = JSON.parse(note.noteText)
        } catch {
          /* skip unparsable note */
        }
      })

      setPatternData(draft)
      // draftData stays null until the renderer finishes RestorePattern and reports back
    } catch {
      setIsError(true)
    } finally {
      setLoading(false)
    }
  }, [galleryId, getTopicDetails, navigate])

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [galleryId])

  const isOwner = useMemo(() => {
    return !!ewUser?.userID && !!topic?.userID && String(ewUser.userID) === String(topic.userID)
  }, [ewUser, topic])

  const updateRenderer = useCallback(() => {
    window.requestAnimationFrame(() => {
      canvaRef.current?.updateRender()
    })
  }, [])

  /**
   * Material/color are room-wide swatches in the studio (matching the wizard's
   * product decision), not the legacy per-mesh click flow — so we pulse the
   * renderer's "apply to all" flag just for this one application. Photos stay
   * per-mesh: pick a photo below, then click a wall in the canvas to place it.
   */
  const updateRendererGlobally = useCallback(() => {
    setApplyToAllActive(true)
    window.requestAnimationFrame(() => {
      canvaRef.current?.updateRender()
      setApplyToAllActive(false)
    })
  }, [])

  const addDraft = useCallback((data: PatternDraft) => {
    setDraftData(data)
  }, [])

  /** Fed to <PatternPreview onEdit>. Mirrors handlePatternEdit in pages/pattern/index.tsx */
  const handlePatternEdit = useCallback(
    (data: Omit<PatternDraft, 'preview'>) => {
      canvaRef.current?.updateOverlayStatus(true)

      if (draftDataRef.current != null) {
        const hasChanges = JSON.stringify(data) !== JSON.stringify(draftDataRef.current)
        if (hasChanges) setChangesCount((c) => c + 1)
      }

      if (templateChanged.current) {
        addDraft({ ...data, accentID: undefined, frameID: undefined })
        setSelectedAssets((prev) => ({ ...prev, accent: undefined, frame: undefined }))
        setPatternData((prev) => ({
          ...prev,
          accentID: undefined,
          frameID: undefined,
          patternFrameID: undefined,
        }))
        templateChanged.current = false
      } else {
        addDraft({ ...data })
      }
    },
    [addDraft],
  )

  /** Fed to <PatternPreview onPatternRestore>. Syncs UI selection with applied renderer state. */
  const handlePatternRestore = useCallback((p: PatternProps) => {
    setSelectedAssets((prev) => ({
      ...prev,
      template: p.currentTemplate ? p.currentTemplate.id : undefined,
      scene: p.currentSceneID,
      accent: p.currentAccentID,
      frame: p.currentFrameID,
      matting: { width: p.mattingWidth * 100 },
      clicked: false,
    }))
  }, [])

  /** Generic selection setter used by the property panel pickers (scene/material/color/frame/print). */
  const setSelection = useCallback(
    (category: StudioSelectionCategory, value: number | string | undefined) => {
      setCurrentTab(category)
      if (category === 'template' && selectedAssets.template !== value) {
        templateChanged.current = true
        setSelectedAssets(
          (prev) =>
            ({
              ...prev,
              accent: undefined,
              frame: undefined,
              matting: { width: 0 },
              [category]: value,
              clicked: true,
            }) as PatternSelections,
        )
      } else {
        setSelectedAssets((prev) => ({ ...prev, [category]: value, clicked: true }) as PatternSelections)
      }
      if (category === 'material' || category === 'color') updateRendererGlobally()
      else updateRenderer()
    },
    [selectedAssets.template, updateRenderer, updateRendererGlobally],
  )

  const setMattingWidth = useCallback(
    (width: number) => {
      setCurrentTab('matting')
      setSelectedAssets((prev) => ({ ...prev, matting: { ...prev.matting, width }, clicked: true }))
      updateRenderer()
    },
    [updateRenderer],
  )

  const selectCategory = useCallback(
    (category: string) => {
      setCurrentTab(category)
      setSelectedAssets((prev) => ({ ...prev, clicked: false }))
      updateRenderer()
    },
    [updateRenderer],
  )

  const updateNote = useCallback(
    async (note: Note) => {
      return updateTopicNote.mutateAsync({
        noteID: note.noteID,
        topicID: galleryId,
        noteText: note.noteText,
        egoList: note.egoList,
      })
    },
    [galleryId, updateTopicNote],
  )

  const saveDraft = useCallback(async () => {
    if (!topic || !draftData) return
    let localNotes = [...topic.notes]

    for (const draftKey in draftData) {
      if (!PatternNoteTypes[draftKey]) continue
      const draftValue = draftData[draftKey as keyof PatternDraft]
      const noteText = draftValue === undefined ? 'null' : JSON.stringify(draftValue)

      const allMatches = localNotes.filter((n) => n.noteType === PatternNoteTypes[draftKey])
      if (allMatches.length > 1) {
        allMatches.sort((a, b) =>
          (b.dateUpdated || b.dateCreated).localeCompare(a.dateUpdated || a.dateCreated),
        )
      }
      const find = allMatches.length > 0 ? allMatches[0] : undefined

      try {
        if (!find) {
          const response = await createTopicNote.mutateAsync({
            topicID: topic.topicID,
            noteName: draftKey,
            noteText,
            noteType: PatternNoteTypes[draftKey],
            egoItems: [],
          })
          if (response?.noteID) {
            localNotes.push({
              noteID: response.noteID,
              noteName: draftKey,
              noteText,
              noteType: PatternNoteTypes[draftKey],
              dateCreated: response.dateCreated || new Date().toISOString(),
              dateUpdated: response.dateUpdated || new Date().toISOString(),
              authorID: ewUser?.userID || '',
              authProfileName: ewUser?.profileName || '',
              authProfileThumbURL: ewUser?.profileThumbURL || '',
              backgroundID: '',
              comments: 0,
              likes: [],
              fontID: '0',
              isThumbsDown: 0,
              isThumbsUp: 0,
              scale: '0',
              thumbsDown: '0',
              thumbsUp: '0',
              egoList: [],
              color: { r: '0', g: '0', b: '0' },
            } as Note)
          }
        } else if (find.noteText !== noteText) {
          find.noteText = noteText
          await updateNote({ ...find, noteText })
        }
      } catch {
        toast.error(`Failed to save ${draftKey}`)
      }
    }

    const updatedTopic = { ...topic, notes: localNotes }
    if (saveMode.current === 0) setTopic(updatedTopic)
    else topicStateRef.current = updatedTopic
  }, [topic, draftData, createTopicNote, ewUser, updateNote])

  const uploadScreenShot = useCallback(() => {
    return new Promise<any>((resolve) => {
      canvaRef.current?.getScreenShotFile(async (file: File) => {
        const upload = await uploadPhoto.mutateAsync(file)
        resolve(upload)
      })
    })
  }, [uploadPhoto])

  const addPhotoToFirstNote = useCallback(
    (obj: Topic) => {
      const { firstNote, topicID } = obj
      uploadScreenShot().then((fileInfo) => {
        if (saveMode.current === 1 || !firstNote) {
          setSaving(false)
          return
        }
        const egoList = [
          {
            sequenceID: '1',
            egoID: fileInfo.egoID,
            photoAlbumURL: fileInfo.egoAlbumURL,
            photoFullURL: fileInfo.egoImageURL,
          },
        ]
        if (firstNote.egoList.length) {
          deleteEgos.mutateAsync([firstNote.egoList[0].egoID]).catch(() => undefined)
        }
        updateTopicNote
          .mutateAsync({ noteID: firstNote.noteID, topicID, noteText: firstNote.noteText, egoList })
          .then(() => {
            setChangesCount(0)
            setTopic({ ...obj, firstNote: { ...obj.firstNote, egoList } })
            if (saveMode.current === 3) navigate('/galleries')
            else if (saveMode.current === 4) navigate(`/galleries/${topicID}/studio`)
            saveMode.current = 0
            setSaving(false)
          })
          .catch(() => setSaving(false))
      })
    },
    [uploadScreenShot, deleteEgos, updateTopicNote, navigate],
  )

  /** Fed to <PatternPreview addScreenshot>. */
  const onScreenshotReady = useCallback(
    (file: File) => {
      if (saveMode.current === 1) {
        canvaRef.current?.getScreenShotFile((f: File) => {
          setScreenshotFile(f)
          setScreenshotModalOpen(true)
        })
        setSaving(false)
        return
      }
      if (topicStateRef.current) addPhotoToFirstNote(topicStateRef.current)
      else setSaving(false)
    },
    [addPhotoToFirstNote],
  )

  const closeScreenshotModal = useCallback(() => setScreenshotModalOpen(false), [])

  const saveNow = useCallback(async () => {
    if (saving) return
    setSaving(true)
    await saveDraft()
    saveMode.current = 2
    canvaRef.current?.saveScreenShot()
  }, [saving, saveDraft])

  const goBack = useCallback(async () => {
    if (changesCount > 0) {
      setSaving(true)
      await saveDraft()
      saveMode.current = 3
      canvaRef.current?.saveScreenShot()
    } else {
      navigate('/galleries')
    }
  }, [changesCount, saveDraft, navigate])

  const rename = useCallback(
    async (text: string) => {
      if (!topic) return
      await updateTopic.mutateAsync({ topicID: topic.topicID, topicName: text })
      setTopic((prev) => (prev ? { ...prev, topicName: text } : prev))
      toast.success('Renamed', { duration: 2000 })
    },
    [topic, updateTopic],
  )

  const togglePrivacy = useCallback(async () => {
    if (!topic) return
    const newPrivacy =
      topic.visibility === PrivacyType.PRIVATE.toString() ? PrivacyType.PUBLIC : PrivacyType.PRIVATE
    toast.loading('Updating…', { id: 'gs-perm' })
    try {
      const permData = await getPermissions.mutateAsync(topic.topicID)
      await updateVisibility.mutateAsync({
        resourceID: topic.topicID,
        cliqueTypes: topic.cliqueTypes,
        cliques: [],
        connections: [],
        accessTypeID: newPrivacy.toString(),
        permissionID: permData.permissionID,
        resourceType: ResourceType.TOPIC,
      })
      setTopic((prev) => (prev ? { ...prev, visibility: newPrivacy.toString() } : prev))
      toast.remove('gs-perm')
      toast.success('Updated', { duration: 2000 })
    } catch (err: any) {
      toast.remove('gs-perm')
      toast.error(err?.toString?.() || 'Failed to update')
    }
  }, [topic, getPermissions, updateVisibility])

  const removeGallery = useCallback(async () => {
    if (!topic) return
    toast.loading('Deleting…', { id: 'gs-delete' })
    try {
      await deleteTopic.mutateAsync({ topicID: topic.topicID })
      toast.remove('gs-delete')
      toast.success('Deleted', { duration: 2000 })
      navigate('/galleries')
    } catch (err: any) {
      toast.remove('gs-delete')
      toast.error(err?.toString?.() || 'Failed to delete')
    }
  }, [topic, deleteTopic, navigate])

  const duplicateGallery = useCallback(async () => {
    if (!topic) return
    const newDraftData = { ...draftData }
    setSaving(true)
    toast.loading('Duplicating…', { id: 'gs-dup' })
    try {
      const result = await createTopic.mutateAsync({
        topicName: `Copy of ${topic.topicName}`,
        topicType: topic.topicType,
        allowContribution: 0,
        topicXData: topic.topicXData || '',
        privacyType: PrivacyType.PRIVATE,
        cliques: [],
        connections: [],
      })
      toast.remove('gs-dup')
      const newTopic = await getTopicDetails.mutateAsync({ topicID: result.topicID })

      let localNotes = [...newTopic.notes]
      for (const draftKey in newDraftData) {
        if (!PatternNoteTypes[draftKey]) continue
        const noteText = JSON.stringify(newDraftData[draftKey as keyof PatternDraft])
        const response = await createTopicNote.mutateAsync({
          topicID: newTopic.topicID,
          noteName: draftKey,
          noteText,
          noteType: PatternNoteTypes[draftKey],
          egoItems: [],
        })
        if (response?.noteID) {
          localNotes.push({ ...response, noteName: draftKey, noteText, noteType: PatternNoteTypes[draftKey] } as Note)
        }
      }

      // Point the (still-mounted, still-showing-the-source-scene) canvas at the
      // new topic so its auto-screenshot lands on the copy, then navigate —
      // same sequence as the legacy duplicatePattern() flow.
      saveMode.current = 4
      const newTopicWithNotes = { ...newTopic, notes: localNotes }
      topicStateRef.current = newTopicWithNotes
      setTopic(newTopicWithNotes)
      setDraftData(newDraftData as PatternDraft)
      toast.success('Duplicated — opening your copy', { duration: 2000 })
      canvaRef.current?.saveScreenShot()
    } catch (err: any) {
      toast.remove('gs-dup')
      toast.error(err?.toString?.() || 'Failed to duplicate')
      setSaving(false)
    }
  }, [topic, draftData, createTopic, getTopicDetails, createTopicNote])

  const onClickTakeScreenshot = useCallback(() => {
    saveMode.current = 1
    canvaRef.current?.saveScreenShot()
  }, [])

  const shareLink = useMemo(() => {
    if (!topic) return ''
    const params = new URLSearchParams()
    params.set('topicID', topic.topicID)
    params.set('topicType', topic.topicType)
    return `${window.location.origin}/preview?${params.toString()}`
  }, [topic])

  const zoomIn = useCallback(() => canvaRef.current?.onZoomInRef(), [])
  const zoomOut = useCallback(() => canvaRef.current?.onZoomOutRef(), [])
  const recenterCamera = useCallback(() => {
    setCameraHorizontalPosition(0.5)
    canvaRef.current?.onCameraHorizontalPositionChangeRef(0.5)
  }, [])
  const setCameraPosition = useCallback((value: number) => {
    setCameraHorizontalPosition(value)
    canvaRef.current?.onCameraHorizontalPositionChangeRef(value)
  }, [])

  return {
    loading,
    saving: saving || createTopicNote.isLoading || updateTopicNote.isLoading,
    isError,
    topic,
    isOwner,
    assets: Assets,
    patternData,
    draftData,
    changesCount,
    isEditMode,
    setIsEditMode,
    currentTab,
    selectedAssets,
    canvaRef,
    setSelection,
    setMattingWidth,
    selectCategory,
    handlePatternEdit,
    handlePatternRestore,
    onScreenshotReady,
    screenshotFile,
    screenshotModalOpen,
    closeScreenshotModal,
    saveNow,
    goBack,
    rename,
    togglePrivacy,
    removeGallery,
    duplicateGallery,
    onClickTakeScreenshot,
    shareLink,
    cameraHorizontalPosition,
    setCameraPosition,
    zoomIn,
    zoomOut,
    recenterCamera,
  }
}

type StudioSelectionCategory = 'template' | 'material' | 'color' | 'frame' | 'print'
